import * as React from "react";
import base64     from "base-64";
import {cx}       from "emotion";

import "./idml-upload-missing-links.scss";

import {XdSentry}       from "@Common/libs/sentry/xd-sentry";
import {ComponentLink,
        Link,
        makeChain}      from "@Common/react/cst2";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {isDevXd}        from "@Common/ui-components/debug/is-dev";

import {ErrorMessage}   from "../components/error-message";
// TODO: Remove reliance on template_admin
import {apis as ccApis} from "../../../../../../../template_admin/client/src/api/apis";
import * as Constants   from "../../../../../../../template_admin/client/src/common/constants";


export const IdmlUploadMissingLinks = ({
    links,
    saveFolder,
    onComplete,
}:{
    links       : any[],
    saveFolder  : string,
    onComplete  : () => void,
}) => (

    makeChain<JSX.Element>()

    // set state vars
    .then("state", (_, next:ComponentLink<typeof UseState<{
        customUploadFiles  : any[],
        errorMessage       : string,
    }>>) => UseState({
        defaultValue: {
            customUploadFiles: [],
            errorMessage: "",
        },
        next,
    }))

    // render view
    .then(({state}) => (
        <div className="idml-upload-missing-links-lk8">
            {!!state.value.errorMessage && (
                <ErrorMessage text={state.value.errorMessage}/>
            )}
            <div className="content">
                <h2>
                    Your template includes {links.length === 1 ? "a file that was" : "files that were"} not embedded.
                </h2>
                <p>
                    You can{" "}
                    <a href="#new-template"
                        onClick={(e) => {e.preventDefault(); window.location.hash = "#new-template"}}
                    >return to upload</a> after updating your IDML or upload the missing files below.
                </p>
                <ul>
                    {links.map((linkFileName, index) => (
                        <label className="custom-file-upload">
                            <input type="file"
                                name={linkFileName}
                                placeholder={linkFileName}
                                key={"link"+index}
                                accept={
                                    ["jpg", "jpeg"].includes(linkFileName.split(".").pop().toLowerCase())
                                    ? "image/jpeg"
                                    : linkFileName.split(".").pop().toLowerCase() === "png"
                                    ? "image/png"
                                    : linkFileName.split(".").pop().toLowerCase() === "pdf"
                                    ? "application/pdf"
                                    : ""
                                }
                                onChange={e => {
                                    const file = e.target.files[0];
                                    const customUploadFile = {
                                        idmlFileName: linkFileName,
                                        userFileName: file?.name,
                                        fileData: file
                                    };

                                    // add the file for uploading upon continue, overwriting existing values if a
                                    // previous selection was already made
                                    let files = state.value.customUploadFiles;
                                    const existingItem = files.find(f => f.idmlFileName === linkFileName);
                                    if (existingItem) Object.assign(existingItem, customUploadFile);
                                    else files.push(customUploadFile);

                                    state.onChange({...state.value, customUploadFiles: files});

                                }}
                                xd-qa-type="fileupload" xd-qa-name={`New Template Upload Missing Link ${linkFileName}`}
                            />
                            <span className="selected-indicator">
                                {!!state.value.customUploadFiles.find(f => f.idmlFileName === linkFileName) && (
                                    <span className="custom-checkbox">
                                        <span className="checkmark"></span>
                                    </span>
                                )}
                            </span>
                            <span className={`file-placeholder ${!!state.value.customUploadFiles.find(f => f.idmlFileName === linkFileName) && 'uploaded'}`}>
                                {linkFileName}
                            </span>
                            <span className="browse-btn">Browse</span>
                        </label>
                    ))}
                </ul>
            </div>
            <div className="button-bar">
                <button className="upload-btn"
                    disabled={links.length > state.value.customUploadFiles.length}
                    onClick={ async (e) => {
                        let error = false;
                        for (const file of state.value.customUploadFiles) {
                            let headers = {
                                filename: file.idmlFileName,
                                templatepath: saveFolder,
                                connection: "keep-alive",
                                authorization: `Basic ${base64.encode(`${Constants.CC_API_USERNAME}:${Constants.CC_API_PASSWORD}`)}`
                            };

                            let formData = new FormData();
                            formData.append("file", file.fileData);

                            const systemConstants = await ccApis.getSystemConstants();

                            const success = await new Promise((resolve) => {
                                const xhr = new XMLHttpRequest();
                                xhr.open("POST", `${systemConstants.DS_SERVER_URL}${Constants.CC_TEMPLATE_UPLOAD_FILES_URL}`, true);
                                for (const [key, value] of Object.entries(headers)) {
                                    xhr.setRequestHeader(key, value);
                                }
                                xhr.onload = () => {
                                    if (xhr.status >= 200 && xhr.status < 300) {
                                        resolve(true);
                                    } else {
                                        if (isDevXd()) console.error(`IdmlUploadMissingLinks File upload returned invalid status. ${xhr.statusText}`);
                                        XdSentry.addBreadcrumb({
                                            category: "idmlupload",
                                            message: "User attempted to upload an link file that received an invalid response.",
                                            level: XdSentry.Severity.Error,
                                            data: {
                                                status: xhr.status,
                                                statusText: xhr.statusText,
                                                requestHeaders: headers,
                                                uploadFileData: formData,
                                            }
                                        });
                                        XdSentry.captureMessage("IDML Upload Link File Error", XdSentry.Severity.Log);
                                        resolve(false);
                                    }
                                };
                                xhr.onerror = (error) => {
                                    if (isDevXd()) console.error(`IdmlUploadMissingLinks File upload failed. ${xhr.statusText}`);
                                    XdSentry.addBreadcrumb({
                                        category: "idmlupload",
                                        message: "User attempted to upload a link file that failed during transmission.",
                                        level: XdSentry.Severity.Error,
                                        data: {
                                            status: xhr.status,
                                            statusText: xhr.statusText,
                                            requestHeaders: headers,
                                            uploadFileData: formData,
                                            error,
                                        }
                                    });
                                    XdSentry.captureMessage("IDML Upload Link File Error", XdSentry.Severity.Log);
                                    resolve(false);
                                };

                                xhr.send(formData);
                            }).catch((error) => {
                                if (isDevXd()) console.error(`IdmlUploadMissingLinks Error uploading ${file.idmlFileName} - ${error}`);
                                XdSentry.addBreadcrumb({
                                    category: "idmlupload",
                                    message: "User attempted to upload a link file that failed during processing.",
                                    level: XdSentry.Severity.Error,
                                    data: {
                                        requestHeaders: headers,
                                        uploadFileData: formData,
                                        error,
                                    }
                                });
                                XdSentry.captureMessage("IDML Upload Link File Error", XdSentry.Severity.Log);
                                error = true;
                            });

                            error = !success;

                        }

                        if (error) {
                            state.onChange({...state.value, errorMessage: "Could not upload link files. Please try again later."});
                            return;
                        }

                        onComplete();

                    }}
                    name="upload_continue"
                    xd-qa-type="button" xd-qa-name="New Template Upload Upload Links"
                >Upload & Continue</button>
            </div>
        </div>
    ))

    .run()

);
